import { Boxes, CheckCircle2, PlayCircle, Settings2 } from 'lucide-react'
import OpenClawLandingPage from '../components/openclaw/OpenClawLandingPage'
import Baidu2DownloadButtons from '../components/openclaw/Baidu2DownloadButtons'
import useBaidu2Tracking from '../hooks/useBaidu2Tracking'

function OpenClawLocalDeploy({ landingPage = 'local-deploy' }) {
  const { bdVid, landingUrl } = useBaidu2Tracking(landingPage)

  return (
    <OpenClawLandingPage
      downloadContent={<Baidu2DownloadButtons theme="amber" bdVid={bdVid} landingUrl={landingUrl} landingPage={landingPage} />}
      theme="amber"
      source="openclaw-local-deploy"
      seo={{
        title: 'OpenClaw本地部署 快速搭建教程',
        description: '面向普通用户整理 OpenClaw 本地部署思路，包含部署准备、关键配置、启动说明以及部署完成后怎么用的基础引导。',
        keywords: 'OpenClaw本地部署, OpenClaw部署教程, OpenClaw怎么用, OpenClaw环境准备, OpenClaw启动说明'
      }}
      hero={{
        eyebrow: 'OpenClaw 本地部署',
        title: 'OpenClaw 本地部署',
        subtitle: '普通用户可读：准备 → 配置 → 启动 → 验证',
        description:
          '如果你关心 OpenClaw 本地部署怎么做、环境需要准备什么、部署完成后怎么快速跑通，这个页面会尽量用更好理解的方式把思路与关键步骤讲清楚。\n不堆一大段命令，更强调关键配置与验证结果，让你知道“做到哪一步算成功”。',
        leadBullets: ['先确认部署目标与准备项', '再完成关键配置与启动步骤', '最后用可视化方式验证并跑通第一轮体验'],
        tags: ['本地部署', '环境准备', '关键配置', '启动说明'],
        guideTitle: '本地部署不等于复杂开发教程',
        guideDescription: '对普通用户来说，本地部署最重要的是知道先准备什么、按什么顺序执行、看到什么结果算成功，以及部署完之后第一步怎么开始使用。',
        guideBullets: [
          '先弄清本地部署的目标：是为了自己本机体验、测试，还是稳定长期使用。',
          '再准备运行环境与必要配置，避免走到一半才发现条件不满足。',
          '最后按“启动成功 → 验证界面 → 开始使用”的顺序检查是否真正跑通。'
        ]
      }}
      screenshotCaption="本地部署完成后，最直观的验证方式就是界面或入口能否正常打开。对普通用户来说，这比抽象的控制台输出更容易理解。"
      highlights={[
        {
          icon: Boxes,
          title: '把部署流程拆成可理解的步骤',
          description: '页面不会假设你是专业开发者，而是把本地部署拆成准备、配置、启动、验证四个阶段。'
        },
        {
          icon: Settings2,
          title: '强调关键配置，而不是堆大量命令',
          description: '你最需要知道的是“哪些配置必须确认”，而不是被一大段命令淹没。'
        },
        {
          icon: CheckCircle2,
          title: '部署完成后马上知道下一步做什么',
          description: '不是部署完就结束，而是会补上“OpenClaw 怎么用”的基础引导，帮助你快速跑通第一轮体验。'
        }
      ]}
      steps={[
        {
          title: '先确认本地部署目的',
          description: '如果只是快速体验，可以优先走最小化部署路径；如果是长期使用，则要更关注环境稳定性和后续维护便利性。'
        },
        {
          title: '准备运行环境与必要资源',
          description: '本地部署前要先确认系统环境、软件依赖、网络条件和必要配置是否具备，否则后面容易卡在细节问题上。'
        },
        {
          title: '完成关键配置后再启动',
          description: '本地部署不是越快敲命令越好，而是先把路径、依赖和核心配置确认好，再启动，成功率会更高。'
        },
        {
          title: '启动后先做可视化验证',
          description: '最适合普通用户的验证方式，是确认界面是否能打开、功能入口是否正常，而不是只看终端有没有报错。'
        }
      ]}
      sections={[
        {
          title: '普通用户做 OpenClaw 本地部署时最容易忽略什么？',
          description: '很多人会直接搜索教程照着执行，但没有先确认自己的本机环境是否适配，最终问题往往出在准备阶段。',
          items: [
            '没有先明确自己的部署目标，导致步骤选错。',
            '没有提前检查系统环境与必需软件。',
            '配置项不知道哪些必须改、哪些可以保留默认。',
            '启动后没有做正确验证，以为“没报错就等于部署好了”。'
          ]
        },
        {
          title: '部署完成后怎么快速开始使用？',
          description: '本地部署成功后，最好不要马上尝试复杂玩法，而是先完成第一轮最基本的使用闭环。',
          items: [
            '先确认客户端或入口能正常打开。',
            '先熟悉主界面和基本功能位置。',
            '先完成一次最简单的功能体验，确认整体链路正常。',
            '确认没问题后，再逐步尝试更多场景。'
          ]
        }
      ]}
      faq={[
        {
          q: 'OpenClaw 本地部署是不是只适合开发者？',
          a: '不一定。只要教程写得足够清楚，普通用户也能理解部署流程。关键在于把准备、配置和验证阶段讲明白。'
        },
        {
          q: '本地部署和直接下载安装有什么区别？',
          a: '直接下载安装更偏立即体验；本地部署更偏自己准备运行环境并跑通。这个页面会重点帮助你理解后者。'
        },
        {
          q: '本地部署成功后，怎么判断 OpenClaw 已经能用了？',
          a: '对普通用户来说，最直接的标准是入口正常、界面正常、基础操作能够跑通。'
        },
        {
          q: '如果我只是想用，不想研究太多技术细节怎么办？',
          a: '这个页面就是按普通用户视角来组织内容的，优先帮你理解需要做什么，而不是强行进入深度技术细节。'
        }
      ]}
      closing={{
        title: '想开始本地部署，或者先下载再体验？',
        description: '如果你已经理解了 OpenClaw 本地部署的大致流程，可以先下载客户端感受产品界面，再决定是否继续做更完整的本地搭建。',
        footerTitle: '让本地部署对普通用户也更容易理解',
        footerDescription: '这个页面不会故意堆很多技术词，而是尽量用更好理解的方式，让来自广告落地的用户知道“部署到底意味着什么，以及我接下来该做什么”。',
        footerTags: ['OpenClaw本地部署', '快速搭建教程', '普通用户可读', '部署后快速跑通']
      }}
    />
  )
}

export default OpenClawLocalDeploy
